import type { LucideIcon } from "lucide-react";
import { Card } from "@/components/ui/card";

interface StaticMetricCardAltProps {
  title: string;
  value: string;
  change?: string;
  changeLabel: string;
  changeLabelIcon?: LucideIcon;
  icon: LucideIcon;
  variant?: "success" | "warning" | "error" | "info";
  onClick?: () => void;
}

const variantStyles = {
  success: {
    background: "bg-success-100", // soft green background
    icon: "text-success-600", // success-600 icon color
  },
  warning: {
    background: "bg-warning-100", // soft orange background
    icon: "text-warning-600", // warning-600 icon color
  },
  error: {
    background: "bg-error-100", // soft red background
    icon: "text-error-600", // error-600 icon color
  },
  info: {
    background: "bg-teal-100", // soft teal background
    icon: "text-teal-600", // teal-600 icon color
  },
};

export function StaticMetricCardAlt({
  title,
  value,
  change,
  changeLabel,
  changeLabelIcon: ChangeLabelIcon,
  icon: Icon,
  variant = "success",
  onClick,
}: StaticMetricCardAltProps) {
  const styles = variantStyles[variant];

  return (
    <Card 
      className={`overflow-hidden border-0 ${onClick ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''}`}
      onClick={onClick}
    >
      {/* Colored Top Section - IMPORTANT: Use className with template literal, NOT inline style */}
      <div className={`relative h-24 p-4 ${styles.background}`}>
        <div className="flex items-center justify-between h-full">
          <div className="flex flex-col justify-center">
            <p className="text-neutral-700 text-sm font-medium">{title}</p>
            <p className="text-neutral-900 text-2xl font-bold mt-1">{value}</p>
          </div>
          <div className={`${styles.icon}`}>
            <Icon className="h-10 w-10" strokeWidth={1.5} />
          </div>
        </div>
      </div>

      {/* White Bottom Section */}
      <div className="p-4 bg-white dark:bg-neutral-900">
        <div className="flex items-center gap-2">
          {change && <span className="text-sm font-medium text-emerald-600">{change}</span>}
          <span className="flex items-center gap-1.5 text-sm text-neutral-500 dark:text-neutral-400">
            {ChangeLabelIcon && <ChangeLabelIcon className="h-3.5 w-3.5" />}
            {changeLabel}
          </span>
        </div>
      </div>
    </Card>
  );
}
